import DriveFolderTree from './drive-folder-tree';
import { useEffect, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  getDriveFoldersForTab,
  getDriveRootIdForDriveTab,
  getDriveRootIdForExplorer,
  UserKeys,
} from '../../../../api/users-api';
import { parseDriveFolderId } from './parseDriveFolderId';
import DriveGrid from './drive-grid';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';

interface DriveGridWithTreeProps {
  userEmail: string;
  primaryDomain: string;
  driveRoles: string[];
  changeLogRoles: string[];
  isAllUsers?: boolean;
  initialDriveRootId?: string | null;
  isExplorer?: boolean;
  canRenderDriveContent?: boolean;
  showChangeLogOverlay?: boolean;
  isChangeLogConfirmed?: boolean;
  onOpenChangeLogDialog?: () => void;
  requiredPermissionName?: string;
}

export default function DriveGridWithTree({
  userEmail,
  primaryDomain,
  driveRoles,
  changeLogRoles,
  isAllUsers,
  initialDriveRootId,
  isExplorer = false,
  canRenderDriveContent = true,
  isChangeLogConfirmed = true,
  onOpenChangeLogDialog = () => {},
  requiredPermissionName = '',
  showChangeLogOverlay = false,
}: DriveGridWithTreeProps) {
  const [selectedFolder, setSelectedFolder] = useState('');

  const {
    data: driveId,
    isLoading: driveIdLoading,
    isFetching: driveIdFetching,
  } = useQuery({
    queryKey: [UserKeys.DriveRootId, userEmail, isExplorer],
    queryFn: () =>
      isExplorer
        ? getDriveRootIdForExplorer(userEmail)
        : getDriveRootIdForDriveTab(userEmail),
    enabled:
      userEmail !== '' &&
      !initialDriveRootId &&
      canRenderDriveContent &&
      isChangeLogConfirmed,
    meta: {
      errorMessage: `There was an issue loading the user's drive data. Please refresh and try again.`,
    },
  });

  const resolvedDriveId = initialDriveRootId || driveId || '';

  //have it selected initially
  useEffect(() => {
    if (resolvedDriveId) {
      setSelectedFolder(resolvedDriveId);
    }
  }, [resolvedDriveId]);

  const isTrashStarredOrSharedWithMe =
    selectedFolder === `${resolvedDriveId}.shared` ||
    selectedFolder === `${resolvedDriveId}.trash` ||
    selectedFolder === `${resolvedDriveId}.starred`;

  //  get all folders in the drive, except for shared, trash, starred
  const {
    data: allFolders,
    fetchNextPage,
    isFetchingNextPage,
    isLoading: isLoadingFolderTree,
  } = useInfiniteQuery({
    queryKey: [UserKeys.DriveFolders, userEmail],
    queryFn: ({ pageParam }) => {
      const { folderPrefix } = parseDriveFolderId(pageParam || '');
      return getDriveFoldersForTab(
        userEmail,
        folderPrefix,
        false,
        false,
        false,
        false,
        'name'
      );
    },
    initialPageParam: resolvedDriveId,
    getNextPageParam: () => {
      return selectedFolder;
    },
    enabled:
      resolvedDriveId !== '' &&
      userEmail !== '' &&
      !isTrashStarredOrSharedWithMe &&
      selectedFolder !== '' &&
      canRenderDriveContent &&
      isChangeLogConfirmed,
    meta: {
      errorMessage: `There was an issue loading the user's drive data. Please refresh and try again.`,
    },
  });

  useEffect(() => {
    if (
      userEmail !== '' &&
      selectedFolder !== '' &&
      !isFetchingNextPage &&
      !allFolders?.pageParams.includes(selectedFolder) &&
      canRenderDriveContent &&
      isChangeLogConfirmed
    ) {
      fetchNextPage();
    }
  }, [
    allFolders?.pageParams,
    fetchNextPage,
    isFetchingNextPage,
    selectedFolder,
    userEmail,
    canRenderDriveContent,
    isChangeLogConfirmed,
  ]);

  const isDriveIdLoading =
    !initialDriveRootId && (driveIdFetching || driveIdLoading);

  const shouldShowChangeLogOverlay =
    showChangeLogOverlay || !canRenderDriveContent;

  return (
    <Stack
      flexDirection="row"
      justifyContent="space-between"
      sx={{ height: '100%', p: 0 }}
      gap={2}
    >
      <Card sx={{ width: 250, overflowY: 'auto' }}>
        <DriveFolderTree
          driveId={resolvedDriveId}
          userEmail={!canRenderDriveContent ? '' : userEmail}
          selectedFolder={selectedFolder}
          setSelectedFolder={setSelectedFolder}
          allFolders={allFolders}
          isLoading={
            isLoadingFolderTree ||
            isDriveIdLoading ||
            (!!resolvedDriveId && !selectedFolder)
          }
        />
      </Card>
      <Box sx={{ flex: 1, position: 'relative' }}>
        <DriveGrid
          userEmail={canRenderDriveContent ? userEmail : ''}
          isLoading={isDriveIdLoading || (!!resolvedDriveId && !selectedFolder)}
          selectedFolder={selectedFolder}
          driveId={canRenderDriveContent ? resolvedDriveId : ''}
          setSelectedFolder={setSelectedFolder}
          primaryDomain={primaryDomain}
          driveRoles={driveRoles}
          allFolders={allFolders}
          changeLogRoles={changeLogRoles}
          isAllUsers={isAllUsers}
          showChangeLogOverlay={shouldShowChangeLogOverlay}
          onOpenChangeLogDialog={onOpenChangeLogDialog}
          requiredPermissionName={requiredPermissionName}
        />
      </Box>
    </Stack>
  );
}
